// A button on the board (Features 2, 21 Oct): one click runs a task the user saved, such as Update ("read my inbox
// and update the deals on this pipeline"), with the board's agent on their computer, in the background. It goes as a
// new chat set aside, so the chat on screen stays as it was. The task is the widget's props.auto, which only the user
// sets (an agent is refused it, as a job's); an agent that sets a button up, or changes it when the user asks, leaves
// its words in props.task, and the user says yes to them once, on the next press. Its last run is the board's (Round 7,
// David: on every device, and how it really ended): the board follows the run's turn on the computer and keeps how it
// went, so every device of the owner's, and this one opened again, says the same, whichever page pressed it.
//
// It is just the button (Round 7, David: "Apple-simple", "it should look like it belongs in macOS, not like a widget"):
// one pill with its words, the width of where it sits and one line tall, no tile, header or line under it. While it
// runs it says so in place ("Updating…"), for a few seconds after it says what it changed ("Updated · 3 changes",
// a click opens the chat it ran in), and a run that did not finish says so until it is opened. Who runs it and where
// is in its tooltip and its settings, which a small gear on hover opens, as a right click opens its menu.

import { useEffect, useState } from "react";
import { create } from "zustand";
import { Check, CircleAlert, Settings } from "lucide-react";
import { BRIEF_DEFAULT, cleanAuto, visibleWords, type BriefAuto } from "../../shared/brief";
import { modelFitsAgent } from "../../shared/agentModels";
import { shortAgo } from "../../shared/format";
import type { ButtonRun, WidgetItem } from "../../shared/types";
import { adoptRunnerTurns } from "../agent/runnerChat";
import { HandsFreeIcon } from "../agent/HandsFree";
import { awayHandsFree, handsFreeDefault } from "../agent/handsFree";
import { HANDS_FREE } from "../../shared/withoutAsking";
import { chat, loadConversation, openChat, openChatById, useChat } from "../agent/store";
import { Dialog } from "../chrome/ui";
import { cx } from "../lib/util";
import { updateProps } from "../state/actions";
import { currentBoard, get, set, toast, useStore } from "../state/store";
import { flush } from "../state/sync";
import type { WP } from "./data";
import { localZone } from "./job";
import { useTick } from "./tick";
import { useSample } from "./WidgetTile";
import { readStoredJson, writeStoredJson } from "../lib/stored";

/**
 * A press in this tab, until the board has its run: what the button says in the moment before the board answers, and
 * how the run went here when the board never took it (the press did not reach it).
 */
interface Pressed {
  chat: string;
  at: number;
  state: "running" | "done" | "failed";
  why?: string | null;
}
const usePressed = create<Record<string, Pressed>>(() => ({}));
const keep = (id: string, p: Pressed) => usePressed.setState({ [id]: p });

/**
 * Runs that did not finish whose chat this browser opened from the button, by the chat they ran in: the button says
 * "Didn't finish" until then, and its own words after. Kept in this browser, so each device says it until it is seen.
 */
const SEEN_KEY = "cb:buttons-seen";
function seenAtStart(): Record<string, string> {
  const v = readStoredJson<unknown>(SEEN_KEY, {});
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, string>) : {};
}
const useSeen = create<Record<string, string>>(() => seenAtStart());
function markSeen(item: string, chatId: string) {
  // the newest few dozen are plenty: one a button
  const next = Object.fromEntries([...Object.entries(useSeen.getState()).filter(([k]) => k !== item), [item, chatId]].slice(-60));
  useSeen.setState(next, true);
  writeStoredJson(SEEN_KEY, next); // private mode: it is seen for this visit
}

const agentName = (a: BriefAuto["agent"]) => (a === "codex" ? "Codex" : "Claude");
const providerOf = (a: BriefAuto["agent"]) => (a === "codex" ? ("openai" as const) : ("anthropic" as const));

/** The button's task as the user saved it, or null before they did. */
export function actionTask(item: WidgetItem): BriefAuto | null {
  const a = cleanAuto(item.props.auto);
  return a?.prompt ? a : null;
}

/** Words an agent left for a button, waiting on the user's yes: null when there are none, or they are what is saved. */
export function suggestedTask(item: WidgetItem): string | null {
  // what the user reads for their OK is what runs: nothing hidden in characters that draw as nothing (review, Round 7)
  const t = typeof item.props.task === "string" ? visibleWords(item.props.task).trim().slice(0, 4000) : "";
  return t && t !== actionTask(item)?.prompt ? t : null;
}

/** The agent a button runs on before the user picks one: the chat's. */
const chatAgent = (): BriefAuto["agent"] => (chat.get().config.provider === "openai" ? "codex" : "claude");

/**
 * Saves the button's task, the user's own from now on, and takes the agent's suggestion away; or who runs it, or
 * whether it runs hands-free (kept only while it is on). The task as saved.
 */
export function saveTask(id: string, patch: Partial<Pick<BriefAuto, "prompt" | "agent">> & { noAsk?: boolean; model?: string | null }): BriefAuto {
  const it = get().items[id];
  const cur = it?.kind === "widget" ? cleanAuto(it.props.auto) : null;
  const { noAsk, model, ...words } = patch;
  const auto: BriefAuto = { ...BRIEF_DEFAULT, ...(cur ?? {}), agent: cur?.agent ?? chatAgent(), ...words, on: true, every: "manual", tz: localZone(), from: Date.now() };
  auto.prompt = auto.prompt.trim().slice(0, 4000);
  if (noAsk === true) auto.noAsk = true;
  else if (noAsk === false) delete auto.noAsk;
  // its own model (Features 4): null goes back to its agent's default, and one of the agent it no longer runs on goes
  if (model) auto.model = model;
  if (model === null || (auto.model && !modelFitsAgent(auto.agent, auto.model))) delete auto.model;
  // Hands-free (Features 4): a button's first save starts it as Settings, Agents says new ones start, and off takes its
  // sending too with it
  if (noAsk === undefined && !cur && handsFreeDefault()) auto.noAsk = true;
  if (!auto.noAsk) delete auto.noAskSend;
  // the agent's suggestion goes once the user saved words of their own; choosing who runs it keeps it to look at
  updateProps(id, patch.prompt !== undefined ? { auto, task: undefined } : { auto }, noAsk === undefined ? "Button task" : noAsk ? "Hands-free on" : "Hands-free off");
  return auto;
}

/** A Button's "Don't ask before sending either" (Features 4): kept only while it runs hands-free, by its owner alone. */
export function saveSends(id: string, on: boolean) {
  const it = get().items[id];
  const cur = it?.kind === "widget" ? cleanAuto(it.props.auto) : null;
  if (!cur?.noAsk) return;
  const auto: BriefAuto = { ...cur };
  if (on) auto.noAskSend = true;
  else delete auto.noAskSend;
  updateProps(id, { auto }, on ? "Hands-free sends too" : "Hands-free asks before sending");
}

/**
 * Opens a button's chat, as picking it in the Chats list does. A run still going on the computer is followed here
 * first, as a reload would follow it (adoptRunnerTurns): pressed on another device, it shows here as it goes, and its
 * question or OK can be answered here.
 */
function openRun(id: string, run?: ButtonRun | null) {
  if (run && run.chat === id && (run.state === "running" || run.state === "needs")) {
    const s = get();
    if (!s.runnerTurns.some((t) => t.turn === run.turn)) set({ runnerTurns: [...s.runnerTurns, { turn: run.turn, agent: run.agent, since: run.at, chat: run.chat, heard: true }] });
    adoptRunnerTurns();
  }
  void openChatById(id).then((r) => {
    if (r === "ok") return openChat();
    toast({ level: "warn", title: "That chat did not open", body: r === "busy" ? "The chat on screen is still replying. Open it from the Chats list when it is done." : r === "room" ? "The chat you're in is too long to keep. Open the button's chat from the Chats list." : r === "offline" ? "The board could not be reached. Try again in a moment." : r === "gone" ? "It was deleted from your chats." : "Open it from the Chats list." });
  });
}

/** Buttons pressed and not yet sent: a second press while the first is on its way starts nothing. */
const pressing = new Set<string>();

/** Words for the agent's context: one line, short, no quotes that would read as the end of them. */
const plain = (s: string, n: number) => s.replace(/[\s"“”<>]+/g, " ").trim().slice(0, n);

/** Runs the button's task now, in a new chat set aside on the user's computer. */
async function press(item: WidgetItem, a: BriefAuto) {
  if (pressing.has(item.id)) return;
  pressing.add(item.id);
  try {
    await pressNow(item, a);
  } finally {
    pressing.delete(item.id);
  }
}

async function pressNow(item: WidgetItem, a: BriefAuto) {
  const label = item.title?.trim() || "Button";
  if (!chat.get().workspaceId) loadConversation(get().workspace?.id ?? null);
  // the task as saved reaches the board first
  await flush().catch(() => {});
  const board = currentBoard(get());
  const extra = [`This message is the board button “${plain(label, 40)}”${board ? ` on “${plain(board.name, 60)}”` : ""}: the user pressed it and is not watching this chat. Do the task without asking what you can find out yourself, and change only what it asks. End with one or two short lines on what you changed, or that nothing needed changing.`];
  const { runAway } = await import("../agent/run");
  // the board keeps the run from here on, for every device (Round 7)
  // on its own model when it has one (Features 4), else its agent's default
  const run = runAway(a.prompt, providerOf(a.agent), extra, item.id, a.model ?? null);
  if (!run) {
    const s = get();
    const r = s.runner;
    // never linked: one line, and the one step that connects the agent (Features 4); linked: what to do there
    const paired = s.keys.find((k) => k.kind === "runner");
    toast({
      level: "warn",
      title: `“${label}” did not run`,
      body: r?.online ? `${a.agent === "codex" ? "Codex" : "Claude Code"} is not ready on ${r.host ?? "your computer"}. Sign in to it there, then press it again.` : paired ? `${paired.host ?? "Your computer"} is off or asleep. Turn it on, then press it again.` : "Buttons run on Claude Code or Codex on your computer, and none is linked yet.",
      action: r?.online || paired ? { label: "Open in the chat", run: () => openChat(a.prompt, { send: false }) } : { label: "Connect your agent", run: () => void import("../onboarding/ConnectAgent").then((m) => m.showConnectAgent()) },
    });
    return;
  }
  // its chat wears the Button's Hands-free, which the board reads from the Button itself for the run (Features 4)
  if (a.noAsk) awayHandsFree(run.id, true);
  keep(item.id, { chat: run.id, at: Date.now(), state: "running" });
  pressing.delete(item.id);
  const end = await run.done;
  keep(item.id, { chat: run.id, at: Date.now(), state: end.ok ? "done" : "failed", why: end.error?.replace(/[.\s]+$/, "") ?? null });
}

/** When a run ended, as the button's tooltip says it: "just now", "5m ago". */
const agoWords = (t: number, now: number) => (shortAgo(t, now) === "now" ? "just now" : `${shortAgo(t, now)} ago`);

/** How long a finished run says what it changed before the button shows its words again. */
const DONE_MS = 5000;

/** A button's first word as it runs and once it is done: Update says Updating… and Updated; any other, Working… and Done. */
const VERBS: Record<string, [string, string]> = {
  update: ["Updating", "Updated"],
  refresh: ["Refreshing", "Refreshed"],
  sync: ["Syncing", "Synced"],
  check: ["Checking", "Checked"],
  tidy: ["Tidying", "Tidied"],
  clean: ["Cleaning", "Cleaned"],
  sort: ["Sorting", "Sorted"],
  send: ["Sending", "Sent"],
  import: ["Importing", "Imported"],
  fetch: ["Fetching", "Fetched"],
};
export function verbsOf(label: string): [string, string] {
  return VERBS[label.trim().split(/\s+/)[0]?.toLowerCase() ?? ""] ?? ["Working", "Done"];
}

/** "3 changes", "1 change", "no changes". */
export const changesWords = (n: number) => (n === 0 ? "no changes" : n === 1 ? "1 change" : `${n} changes`);

export function ActionButton({ item, readOnly }: WP) {
  useTick(60_000);
  const sample = useSample();
  const a = actionTask(item);
  const suggested = suggestedTask(item);
  // the board's run is every device's; a press here shows at once, before the board has it, and stays when the board
  // never took it. The board's stands once it is this press's or began after it
  const run = useStore((s) => s.buttonRuns[item.id]) as ButtonRun | undefined;
  const offset = useStore((s) => s.serverOffset);
  const pressed = usePressed((s) => s[item.id]);
  const mine = pressed && pressed.chat !== run?.chat && !(run && run.at >= pressed.at + offset) ? pressed : null;
  const away = useChat((s) => (mine ? s.away.find((x) => x.id === mine.chat) : undefined));
  const seen = useSeen((s) => s[item.id]);
  const [asking, setAsking] = useState<string | null>(null);
  const label = item.title?.trim() || "Run";
  const [doing, did] = verbsOf(label);
  const who = agentName(run && !mine ? run.agent : (a?.agent ?? "claude"));
  const state = sample ? "idle" : mine ? (away?.state === "needs_you" ? "needs" : away || mine.state === "running" ? "working" : mine.state) : run ? (run.state === "running" ? "working" : run.state) : "idle";
  const chatId = mine?.chat ?? run?.chat ?? null;
  const why = mine ? mine.why : run?.why;
  const changes = mine ? undefined : run?.changes;
  // when it ended: the board's clock for the board's run, so every device says the same time
  const ended = mine ? { at: mine.at, now: Date.now() } : run ? { at: run.end ?? run.at, now: Date.now() + offset } : null;
  const own = useStore((s) => !!s.runner?.ownMcps?.[a?.agent ?? "claude"]?.on);
  const host = useStore((s) => s.runner?.host ?? s.runner?.computers?.find((c) => c.main)?.host ?? null);
  // a coworker the board is shared with to edit: a button's task is the owner's, and the board keeps nothing a coworker
  // types for it, so it says so, with nothing to press or fill in (Round 7 follow-up)
  const coworker = useStore((s) => !!s.guest);
  const owner = !readOnly && !sample && !coworker;
  const noAsk = owner && !!cleanAuto(item.props.auto)?.noAsk;
  const sendsToo = noAsk && !!cleanAuto(item.props.auto)?.noAskSend;
  // a Button an agent set up starts hands-free on the user's first yes when Settings, Agents says new ones do
  const freeOnYes = noAsk || (owner && !cleanAuto(item.props.auto) && handsFreeDefault());
  // what it says now: working, waiting on the user, what it changed a moment ago, a run that did not finish and that
  // this browser has not opened yet, or its own words
  const fresh = state === "done" && !!ended && ended.now - ended.at < DONE_MS;
  const face = state === "working" || state === "needs" ? state : fresh ? "done" : state === "failed" && chatId && seen !== chatId ? "failed" : "rest";
  // "Updated" for a few seconds, then its words again
  const [, redraw] = useState(0);
  const endAt = ended?.at ?? 0;
  useEffect(() => {
    if (face !== "done" || !ended) return;
    const t = setTimeout(() => redraw((n) => n + 1), Math.max(0, DONE_MS - (ended.now - ended.at)) + 40);
    return () => clearTimeout(t);
  }, [face, endAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const openSettings = () => set({ selection: [item.id], inspect: true, editing: null });
  const look = () => {
    if (chatId) openRun(chatId, mine ? null : run);
  };

  const click = () => {
    if (readOnly || sample) return;
    if (coworker) return void toast({ level: "info", title: "Only the board's owner sets this button up." });
    if (face === "failed" && chatId) markSeen(item.id, chatId);
    if (face !== "rest" && chatId) return look();
    // new words from an agent wait on the user's yes, even when the button runs without asking (Round 7)
    if (suggested) return setAsking(suggested);
    if (a) return void press(item, a);
    openSettings();
  };

  // the tooltip holds what the line under it used to, a short line each: who runs it and where, and how its last run went
  const where = host ? ` on ${host}` : " on your computer";
  const last = run && !mine && ended && (run.state === "done" || run.state === "failed") ? (run.state === "done" ? `${did} ${agoWords(ended.at, ended.now)}${changes !== undefined ? ` · ${changesWords(changes)}` : ""}` : `Didn't finish, ${agoWords(ended.at, ended.now)}`) : null;
  const shortWhy = why ? (why.length > 90 ? `${why.slice(0, 89).replace(/\s+\S*$/, "")}…` : why) : null;
  const tip = sample
    ? undefined
    : readOnly
      ? "Only the board's owner can run it"
      : coworker
        ? "Only the board's owner sets this button up."
        : face === "working"
          ? `${who} is on it. Click to watch.`
          : face === "needs"
            ? `${who} needs your OK to go on.\nClick to answer.`
            : face === "done"
              ? "Click to see what it did."
              : face === "failed"
                ? `${shortWhy ? `Didn't finish: ${shortWhy}.` : "Didn't finish."}\nClick to see why.`
                : suggested
                  ? a
                    ? "An agent changed what it does.\nPress it to check the new task first."
                    : "Set up by an agent.\nPress it to check the task first."
                  : !a
                    ? "Say what it does."
                    : [`${who}${where}`, own ? null : "Board tools only", noAsk ? (sendsToo ? HANDS_FREE.tipSends : HANDS_FREE.tip) : null, last].filter(Boolean).join("\n");
  const text = face === "working" ? `${doing}…` : face === "needs" ? "Needs your OK" : face === "failed" ? "Didn't finish" : face === "done" ? did : label;

  return (
    <div className={cx("ab", `is-${face}`, face === "rest" && !a && !suggested && owner && "is-unset", face === "rest" && suggested && owner && "is-new")}>
      <button
        className="ab-btn"
        onClick={click}
        disabled={readOnly && !sample}
        aria-disabled={coworker || undefined}
        aria-busy={face === "working"}
        data-tip={tip}
        data-drags
      >
        {face === "working" && <span className="spinner ab-spin" aria-hidden />}
        {(face === "needs" || face === "failed") && <CircleAlert className="ab-ico" aria-hidden />}
        {face === "done" && <Check className="ab-ico" aria-hidden />}
        <span className="ab-label">
          {text}
          {face === "done" && changes !== undefined && <span className="ab-count"> · {changesWords(changes)}</span>}
        </span>
        {/* Hands-free (Features 4): a quiet mark at its words' end, said in full on hover (new words wait for a yes first) */}
        {noAsk && face === "rest" && !suggested && (
          <span className="ab-auto" role="img" aria-label={sendsToo ? HANDS_FREE.tipSends : HANDS_FREE.tip}>
            <HandsFreeIcon />
          </span>
        )}
      </button>
      {owner && (
        <button className="ab-gear" aria-label={`Settings for “${label}”`} data-tip="Settings" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => (e.stopPropagation(), openSettings())}>
          <Settings aria-hidden />
        </button>
      )}
      {asking !== null && (
        <Dialog
          open
          onClose={() => setAsking(null)}
          width={460}
          title={`Run “${label}”?`}
          footer={
            <>
              <button
                className="btn btn-sm"
                onClick={() => {
                  setAsking(null);
                  openSettings();
                }}
              >
                Change it
              </button>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  setAsking(null);
                  void press(item, saveTask(item.id, { prompt: asking }));
                }}
              >
                Run it
              </button>
            </>
          }
        >
          <p className="ab-ask-lead">
            {a ? "An agent changed what this button does. From now on each press runs this" : "An agent set this button up. Each press runs this"} with {agentName(cleanAuto(item.props.auto)?.agent ?? chatAgent())} on your computer:
          </p>
          <blockquote className="ab-ask-task">{asking}</blockquote>
          {a && (
            <p className="ab-ask-was">
              <span>It was</span> {a.prompt}
            </p>
          )}
          <p className="ab-ask-foot">
            {freeOnYes ? "It runs hands-free, so check the words before you say yes. " : ""}Say yes once and it is yours: later presses run it straight away. You can change it in the button's settings.
          </p>
        </Dialog>
      )}
    </div>
  );
}
